import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatErr, downloadCsv } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFeature } from "@/contexts/FeaturesContext";
import { useI18n } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, AlertTriangle, CalendarClock, Package, Users, ShoppingCart, Truck, Building2, BarChart3, Sparkles, ClipboardList, Download, Trophy, Wallet, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import DashboardAlerts from "@/components/DashboardAlerts";
import BranchKpis from "@/components/BranchKpis";

const KPI = ({ label, value, icon: Icon, tone = "primary", subValue, testid, delta, deltaLabel = "أمس", to }) => {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`w-8 h-8 rounded-md grid place-items-center transition-transform group-hover:scale-110 ${
          tone === "danger" ? "bg-destructive/10 text-destructive" :
          tone === "success" ? "bg-emerald-50 text-emerald-600" :
          tone === "warning" ? "bg-amber-50 text-amber-600" :
          "bg-primary/10 text-primary"
        }`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-3 text-2xl font-heading font-bold tabular-nums">{value}</div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        {subValue && <span className="text-xs text-muted-foreground">{subValue}</span>}
        {delta !== null && delta !== undefined && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`${testid}-delta`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}% {deltaLabel}
          </span>
        )}
      </div>
      {to && (
        <div className="mt-2 text-[10px] text-primary/70 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <span>افتح التفاصيل</span>
          <ArrowLeft className="w-3 h-3" />
        </div>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="block group" data-testid={testid}>
        <Card className="p-5 border-border hover-lift cursor-pointer hover:border-primary/50 hover:shadow-md transition-all h-full">
          {inner}
        </Card>
      </Link>
    );
  }
  return (
    <Card className="p-5 border-border hover-lift" data-testid={testid}>
      {inner}
    </Card>
  );
};

export default function Dashboard() {
  const { currentCompany, companyId } = useAuth();
  const reportsEnabled = useFeature("reports");
  const advancedReportsEnabled = useFeature("advanced_reports");
  const cashierPosEnabled = useFeature("cashier_pos");
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [trend, setTrend] = useState([]);
  const [trendMeta, setTrendMeta] = useState({ current_total: 0, previous_total: 0, change_pct: null });
  const [deltaRange, setDeltaRange] = useState(() => localStorage.getItem("delta_range") || "yesterday");
  const [catSales, setCatSales] = useState([]);
  const [catRangeDays, setCatRangeDays] = useState(() => +(localStorage.getItem("cat_sales_days") || 30));
  const [ranking, setRanking] = useState([]);
  const [fin, setFin] = useState(null);

  const load = async () => {
    if (!companyId) return;
    try {
      const dRes = await api.get(`/reports/dashboard?range=${deltaRange}`);
      api.get("/dashboard/financial").then(r => setFin(r.data)).catch(() => {});
      setData(dRes.data);
      if (!reportsEnabled) {
        setTrend([]); setCatSales([]); setRanking([]);
        return;
      }
      const [tRes, cRes] = await Promise.all([
        api.get("/reports/sales-trend?days=7"),
        api.get(`/reports/category-sales?days=${catRangeDays}&top=8`),
      ]);
      setTrend((tRes.data.items || []).map(i => ({
        day: new Date(i.date).toLocaleDateString("ar-EG", { weekday: "short" }),
        total: i.total, count: i.count,
      })));
      setTrendMeta({
        current_total: tRes.data.current_total || 0,
        previous_total: tRes.data.previous_total || 0,
        change_pct: tRes.data.change_pct,
      });
      setCatSales(cRes.data.items || []);
      // Load rep ranking (best-effort, doesn't block dashboard)
      try { const rk = await api.get("/reports/rep-ranking"); setRanking(rk.data.top || []); } catch {}
    } catch {}
  };
  useEffect(() => { load(); }, [companyId, deltaRange, catRangeDays, reportsEnabled]); // eslint-disable-line
  const toggleRange = () => {
    const next = deltaRange === "yesterday" ? "last_week" : "yesterday";
    localStorage.setItem("delta_range", next);
    setDeltaRange(next);
  };
  const cycleCatRange = () => {
    const next = catRangeDays === 7 ? 30 : (catRangeDays === 30 ? 90 : 7);
    localStorage.setItem("cat_sales_days", String(next));
    setCatRangeDays(next);
  };
  useEffect(() => { load(); }, [companyId]);

  const seedDemo = async () => {
    if (!companyId) return;
    if (!confirm(t("dash_confirm_seed"))) return;
    setSeeding(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/seed-demo`);
      if (data.skipped) toast.info(data.message);
      else toast.success(`${data.message} — ${data.products} صنف، ${data.invoices} فاتورة`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSeeding(false); }
  };

  if (!companyId) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center p-8 border border-border rounded-md bg-card">
        <Building2 className="w-10 h-10 mx-auto text-muted-foreground" />
        <h2 className="text-xl font-heading font-bold mt-4">{t("no_company")}</h2>
        <p className="text-sm text-muted-foreground mt-2">{t("no_company_desc")}</p>
        <Link to="/companies" className="inline-block mt-4 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm">{t("manage_companies")}</Link>
      </div>
    );
  }

  const cur = currentCompany?.base_currency || "SYP";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <SubscriptionBanner />
      <DashboardAlerts />
      <BranchKpis />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{t("dashboard")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("overview_of")} {currentCompany?.name} {t("today")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {reportsEnabled && <>
            <Button onClick={toggleRange} variant="outline" size="sm" className="gap-1" data-testid="delta-range-toggle" title={t("dash_compare_toggle")}>
              <BarChart3 className="w-4 h-4" /> {t("dash_vs")} {deltaRange === "yesterday" ? t("yesterday") : t("dash_week")}
            </Button>
            <Button asChild variant="outline" size="sm" className="gap-1" data-testid="jump-z-report">
              <Link to="/reports"><ClipboardList className="w-4 h-4" /> {t("dash_z_report")}</Link>
            </Button>
          </>}
          {!data?.products_count && (
            <Button onClick={seedDemo} disabled={seeding} size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="seed-demo-btn">
              <Sparkles className="w-4 h-4" /> {seeding ? t("dash_creating") : t("dash_seed_demo")}
            </Button>
          )}
        </div>
      </div>

      {reportsEnabled && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testid="kpi-sales" to="/reports?tab=sales" label={t("today_sales")} value={fmtMoney(data?.today_sales, cur)} icon={TrendingUp} tone="success" subValue={`${data?.today_sales_count || 0} ${t("invoices_count")}`} delta={data?.sales_delta_pct} deltaLabel={data?.range_label} />
        <KPI testid="kpi-purchases" to="/reports?tab=purchases" label={t("today_purchases")} value={fmtMoney(data?.today_purchases, cur)} icon={TrendingDown} subValue={`${data?.today_purchases_count || 0} ${t("invoices_count")}`} delta={data?.purchases_delta_pct} deltaLabel={data?.range_label} />
        <KPI testid="kpi-low-stock" to="/reports?tab=alerts" label={t("low_stock_items")} value={data?.low_stock_count || 0} icon={AlertTriangle} tone="danger" subValue={t("need_reorder")} />
        <KPI testid="kpi-expiring" to="/reports?tab=alerts" label={t("expiring_items")} value={data?.expiring_count || 0} icon={CalendarClock} tone="warning" subValue={t("within_60d")} />
      </div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {reportsEnabled && advancedReportsEnabled && <KPI testid="kpi-net-profit" to="/reports?tab=pnl" label={t("dash_net_profit")} value={fmtMoney(data?.today_net_profit, cur)} icon={Wallet} tone={(data?.today_net_profit || 0) >= 0 ? "success" : "danger"} subValue={`${t("dash_total")}: ${fmtMoney(data?.today_gross_profit, cur)}`} delta={data?.net_profit_delta_pct} deltaLabel={data?.range_label} />}
        <KPI testid="kpi-products" to="/products" label={t("products_count")} value={data?.products_count || 0} icon={Package} />
        <KPI testid="kpi-customers" to="/customers" label={t("customers_count")} value={data?.customers_count || 0} icon={Users} />
        {reportsEnabled && <KPI testid="kpi-revenue" to="/reports?tab=sales" label={t("today_revenue")} value={fmtMoney(data?.today_revenue, cur)} icon={ShoppingCart} tone="success" delta={data?.revenue_delta_pct} />}
      </div>

      {fin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="financial-health">
          <Card className="p-5 border-emerald-200 bg-emerald-50/40" data-testid="fin-receivables">
            <div className="flex items-center gap-2 text-emerald-700 mb-1"><TrendingUp className="w-4 h-4" /><span className="text-sm font-bold">الذمم المدينة (لنا)</span></div>
            <div className="text-2xl font-black tabular-nums text-emerald-800">{fmtMoney(fin.receivables, cur)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">مستحقات لك على العملاء</div>
          </Card>
          <Card className="p-5 border-rose-200 bg-rose-50/40" data-testid="fin-payables">
            <div className="flex items-center gap-2 text-rose-700 mb-1"><TrendingDown className="w-4 h-4" /><span className="text-sm font-bold">الذمم الدائنة (علينا)</span></div>
            <div className="text-2xl font-black tabular-nums text-rose-800">{fmtMoney(fin.payables, cur)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">مستحقات عليك للموردين</div>
          </Card>
          <Card className="p-5 border-indigo-200 bg-indigo-50/40" data-testid="fin-liquidity">
            <div className="flex items-center gap-2 text-indigo-700 mb-1"><Wallet className="w-4 h-4" /><span className="text-sm font-bold">السيولة (الصناديق)</span></div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
              {(fin.liquidity || []).map((l, i) => (
                <span key={i} className="text-lg font-black tabular-nums text-indigo-800">{Math.round(l.balance).toLocaleString("ar-EG")} <span className="text-xs font-medium">{l.currency}</span></span>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">أرصدة الصناديق النقدية الحالية</div>
          </Card>
        </div>
      )}

      {reportsEnabled && trend.length > 0 && (
        <Card className="p-4" data-testid="sales-trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase text-muted-foreground">{t("dash_sales_last7")}</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="text-lg font-heading font-bold tabular-nums">{fmtMoney(trendMeta.current_total, cur)}</div>
                {trendMeta.change_pct !== null && (
                  <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded ${trendMeta.change_pct >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="trend-delta-badge">
                    {trendMeta.change_pct >= 0 ? "▲" : "▼"} {Math.abs(trendMeta.change_pct)}% {t("dash_vs_prev_week")}
                  </span>
                )}
                {trendMeta.change_pct === null && trendMeta.previous_total === 0 && (
                  <span className="text-xs text-muted-foreground">— {t("dash_no_prev_sales")}</span>
                )}
              </div>
            </div>
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div style={{ width: "100%", height: 180, minWidth: 200, minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={180}>
              <LineChart data={trend} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v)=>v>=1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v)=>fmtMoney(v, cur)} labelStyle={{fontWeight:"bold"}} />
                <Line type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {reportsEnabled && ranking.length > 0 && (
        <Card className="p-5" data-testid="rep-ranking-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("dash_leaderboard")}</div>
              <div className="mt-1 text-lg font-heading font-bold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" /> {t("dash_top_reps")}
              </div>
            </div>
            <Link to="/reports/sales-rep-returns" className="text-xs text-primary hover:underline">{t("dash_full_report")} →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {ranking.map((r, i) => {
              const medals = ["🥇", "🥈", "🥉", "🎖️", "🎖️"];
              return (
                <div key={r.id} className={`p-3 rounded-lg border ${i === 0 ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 shadow-md" : i === 1 ? "bg-gradient-to-br from-slate-50 to-gray-50 border-slate-300" : i === 2 ? "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300" : "bg-background"}`} data-testid={`rank-${i}`}>
                  <div className="text-2xl">{medals[i]}</div>
                  <div className={`font-bold mt-1 truncate ${i === 0 ? "text-amber-800" : ""}`} title={r.name}>{r.name}</div>
                  <div className="text-lg font-heading font-bold tabular-nums mt-1">{r.total.toLocaleString("ar-EG")}</div>
                  <div className="text-[10px] text-muted-foreground">{r.count} {t("dash_invoice")} · {t("dash_last7")}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {reportsEnabled && catSales.length > 0 && (
        <Card className="p-5" data-testid="category-sales-card">          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("dash_top_cats")}</div>
              <div className="mt-1 text-lg font-heading font-bold">{t("dash_sales_by_cat")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("dash_last")} {catRangeDays === 7 ? t("dash_week") : catRangeDays === 30 ? t("dash_month") : t("dash_3months")} · {t("dash_total")} {fmtMoney(catSales.reduce((s, x) => s + x.total, 0), cur)}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => {
                if (!catSales.length) return;
                const headers = ["الترتيب", "المجموعة", "المبيعات", "القطع", "النسبة%"];
                const total = catSales.reduce((s, x) => s + x.total, 0) || 1;
                const rows = catSales.map((c, i) => [i + 1, c.name, Math.round(c.total), Math.round(c.qty), ((c.total / total) * 100).toFixed(1)]);
                downloadCsv(`category_sales_${catRangeDays}d.csv`, [headers, ...rows]);
              }} className="gap-1" data-testid="cat-sales-export-btn" title="تصدير Excel">
                <Download className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={cycleCatRange} className="gap-1" data-testid="cat-range-toggle">
                <BarChart3 className="w-3.5 h-3.5" />
                {catRangeDays === 7 ? t("dash_days7") : catRangeDays === 30 ? t("dash_days30") : t("dash_days90")}
              </Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <div style={{ width: "100%", height: 260, minWidth: 200, minHeight: 260 }} data-testid="category-sales-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={260}>
                <PieChart>
                  <Pie
                    data={catSales}
                    dataKey="total"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={2}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                  >
                    {catSales.map((c, i) => (
                      <Cell key={i} fill={c.color || "#3B82F6"} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v)=>fmtMoney(v, cur)} labelStyle={{fontWeight:"bold"}} contentStyle={{background:"hsl(var(--card))", borderColor:"hsl(var(--border))"}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {catSales.map((c, i) => {
                const total = catSales.reduce((s, x) => s + x.total, 0) || 1;
                const pct = (c.total / total) * 100;
                return (
                  <div key={c.name} className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-accent transition" data-testid={`cat-slice-${i}`}>
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: c.color || "#3B82F6" }} />
                    <span className="flex-1 truncate font-semibold">{c.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{Math.round(c.qty).toLocaleString("ar-EG")} {t("dash_piece")}</span>
                    <span className="font-bold tabular-nums w-24 text-end">{fmtMoney(c.total, cur)}</span>
                    <span className="text-[10px] text-muted-foreground w-10 text-end tabular-nums">{pct.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}


      <div className="grid md:grid-cols-3 gap-4">
        {cashierPosEnabled && <Link to="/pos" className="block p-6 border border-border rounded-md bg-card hover-lift">
          <ShoppingCart className="w-6 h-6 text-primary" />
          <div className="mt-3 font-heading font-bold">{t("quick_sale")}</div>
          <div className="text-xs text-muted-foreground mt-1">{t("quick_sale_desc")}</div>
        </Link>}
        <Link to="/products" className="block p-6 border border-border rounded-md bg-card hover-lift">
          <Package className="w-6 h-6 text-primary" />
          <div className="mt-3 font-heading font-bold">{t("manage_products")}</div>
          <div className="text-xs text-muted-foreground mt-1">{t("manage_products_desc")}</div>
        </Link>
        {reportsEnabled && <Link to="/reports" className="block p-6 border border-border rounded-md bg-card hover-lift">
          <BarChart3 className="w-6 h-6 text-primary" />
          <div className="mt-3 font-heading font-bold">{t("reports")}</div>
          <div className="text-xs text-muted-foreground mt-1">{t("reports_desc")}</div>
        </Link>}
      </div>
    </div>
  );
}
